import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  useWindowDimensions,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { ReportAccessGuard } from '../../components/ReportAccessGuard';
import { ReportDownloadProgressView } from '../../components/ReportDownloadProgress';
import { ScreenHeader } from '../../components/ScreenHeader';
import { REPORT_ACCESS_CODES } from '../../config/reportCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { routes } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useReportDownloads } from '../../context/ReportDownloadContext';
import { getEmployeeOverallWeeklyReport } from '../../services/attendanceService';
import type { WeeklyAttendanceEmployeeReport } from '../../types/weeklyAttendanceReport';
import type { ReportFileFormat } from '../../types/report';
import { attendanceErrorMessage } from '../../utils/attendanceErrors';
import {
  EMPLOYEE_OVERALL_WEEKLY_REPORT_CODE,
  overallWeekBounds,
  overallWeeklyColumns,
  overallWeeklyDayStatus,
  overallWeeklyDaysWorked,
  overallWeeklyLeaveDays,
  overallWeeklyLopDays,
  overallWeeklyWorkHours,
  shiftOverallWeek,
} from '../../utils/employeeOverallWeeklyReport';
import { hasPermission } from '../../utils/permissions';
import { reportErrorMessage } from '../../utils/reportErrors';
import { formFactorForWidth } from '../../utils/responsiveLayout';
import { formatWeekRangeLabel } from '../../utils/weeklyAttendanceReport';

type Props = NativeStackScreenProps<
  RootStackParamList,
  'EmployeeOverallWeeklyReport'
>;

const PAGE_SIZE = 20;

/**
 * Column widths per form factor. The backend PDF (SCREEN_LAYOUT in
 * employee-overall-weekly-report.service.js) uses the desktop widths.
 */
export function columnWidths(width: number) {
  const factor = formFactorForWidth(width);
  if (factor === 'mobile') {
    return { employee: 148, day: 136, total: 112, daysWorked: 96, leave: 96, lop: 96 };
  }
  if (factor === 'tablet') {
    return { employee: 180, day: 156, total: 132, daysWorked: 110, leave: 110, lop: 110 };
  }
  return { employee: 210, day: 176, total: 148, daysWorked: 124, leave: 124, lop: 124 };
}

export function statusColor(
  status: string,
  palette: ColorPalette,
): string {
  const label = status.toLowerCase();
  if (label === '—' || label === '-' || label === '') return palette.textSecondary;
  if (label.includes('holiday') || label.includes('weekly off')) {
    return palette.textSecondary;
  }
  if (label.includes('leave') || label.includes('wfh')) return palette.info;
  if (
    label.includes('absent') ||
    label.includes('lop') ||
    label === 'missing checkout'
  ) {
    return palette.error;
  }
  if (label.includes('late') || label.includes('short')) return palette.warning;
  if (label.includes('present') || label.includes('checked in')) {
    return palette.success;
  }
  return palette.textPrimary;
}

function EmployeeOverallWeeklyReportBody({ navigation }: Props) {
  const palette = useAppColors();
  const { permissions } = useAuth();
  const downloads = useReportDownloads();
  const { width } = useWindowDimensions();
  const columns = columnWidths(width);
  const defaults = useMemo(() => overallWeekBounds(), []);
  const [week, setWeek] = useState(defaults);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [items, setItems] = useState<WeeklyAttendanceEmployeeReport[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadNotice, setDownloadNotice] = useState<string | null>(null);

  const canGoBack =
    typeof navigation.canGoBack === 'function' && navigation.canGoBack();
  const canExport = hasPermission(permissions, PERMISSIONS.REPORT_EXPORT);
  const dayColumns = useMemo(
    () => overallWeeklyColumns(week.weekStart),
    [week.weekStart],
  );

  const activeProgress = useMemo(
    () =>
      downloads.activeDownloads.find(
        item =>
          item.downloadId.startsWith(`${EMPLOYEE_OVERALL_WEEKLY_REPORT_CODE}:`) &&
          (item.phase === 'preparing' ||
            item.phase === 'downloading' ||
            item.phase === 'saving'),
      ) ?? null,
    [downloads.activeDownloads],
  );

  const fetchReport = useCallback(
    async (nextPage: number, isRefresh = false) => {
      if (!isRefresh) setLoading(true);
      setError(null);
      try {
        const result = await getEmployeeOverallWeeklyReport({
          weekStart: week.weekStart,
          weekEnd: week.weekEnd,
          search: appliedSearch,
          page: nextPage,
          pageSize: PAGE_SIZE,
        });
        setItems(result.items);
        setPage(result.pagination.page);
        setTotalPages(Math.max(result.pagination.totalPages, 1));
        setTotalRecords(result.pagination.totalRecords);
      } catch (caught) {
        setError(
          attendanceErrorMessage(
            caught,
            'Unable to load the employee overall weekly report.',
          ),
        );
        setItems([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [appliedSearch, week.weekEnd, week.weekStart],
  );

  useEffect(() => {
    void fetchReport(1, false);
  }, [fetchReport]);

  const handleDownload = async (format: Extract<ReportFileFormat, 'XLSX' | 'PDF'>) => {
    setDownloadError(null);
    setDownloadNotice(null);
    if (!canExport) {
      setDownloadError('You do not have permission to export reports.');
      return;
    }
    if (totalRecords === 0 && items.length === 0) {
      setDownloadError('No report data is available to download for this week.');
      return;
    }
    if (downloads.isDownloadInFlight(EMPLOYEE_OVERALL_WEEKLY_REPORT_CODE, format)) {
      setDownloadError('A download is already in progress.');
      return;
    }
    try {
      const result = await downloads.startDownload({
        reportCode: EMPLOYEE_OVERALL_WEEKLY_REPORT_CODE,
        reportName: 'Employee Overall Weekly Report',
        format,
        filters: {},
        employeeOverallWeeklyQuery: {
          weekStart: week.weekStart,
          weekEnd: week.weekEnd,
          search: appliedSearch || undefined,
        },
      });
      if (Platform.OS === 'web') {
        setDownloadNotice(`Downloaded ${result.fileName}`);
        return;
      }
      navigation.navigate(routes.reportDownloadResult, {
        downloadId: result.downloadId,
      });
    } catch (err) {
      setDownloadError(reportErrorMessage(err, 'The report could not be saved.'));
    }
  };

  const rangeStart = totalRecords === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalRecords);
  const canDownload = !loading && !activeProgress;

  return (
      <AppScreen contentStyle={styles.screen} webLayout="wide">
        <ScreenHeader
          title="Employee Overall Weekly Report"
          onBack={canGoBack ? () => navigation.goBack() : undefined}
        />
        <View style={styles.body}>
          <View
            style={[
              styles.weekBar,
              { backgroundColor: palette.surface, borderColor: palette.border },
            ]}
          >
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Previous week"
              onPress={() => setWeek(current => shiftOverallWeek(current.weekStart, -1))}
              hitSlop={8}
            >
              <Text style={[styles.chevron, { color: palette.primary }]}>‹</Text>
            </Pressable>
            <Text style={[styles.weekLabel, { color: palette.textPrimary }]}>
              {formatWeekRangeLabel(week.weekStart, week.weekEnd)}
            </Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Next week"
              onPress={() => setWeek(current => shiftOverallWeek(current.weekStart, 1))}
              hitSlop={8}
            >
              <Text style={[styles.chevron, { color: palette.primary }]}>›</Text>
            </Pressable>
          </View>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="This week"
            onPress={() => setWeek(overallWeekBounds())}
          >
            <Text style={[styles.thisWeek, { color: palette.primary }]}>This week</Text>
          </Pressable>
          <TextInput
            accessibilityLabel="Search Employee"
            placeholder="Search Employee"
            placeholderTextColor={palette.textMuted}
            value={search}
            onChangeText={setSearch}
            onSubmitEditing={() => setAppliedSearch(search.trim())}
            returnKeyType="search"
            style={[
              styles.search,
              {
                backgroundColor: palette.surface,
                borderColor: palette.border,
                color: palette.textPrimary,
              },
            ]}
          />

          {canExport ? (
            <View style={styles.downloadRow}>
              <AppButton
                title="Download Excel"
                variant="secondary"
                accessibilityLabel="Download Report Excel"
                onPress={() => {
                  void handleDownload('XLSX');
                }}
                disabled={!canDownload}
                loading={downloads.isDownloadInFlight(
                  EMPLOYEE_OVERALL_WEEKLY_REPORT_CODE,
                  'XLSX',
                )}
                style={styles.downloadButton}
              />
              <AppButton
                title="Download PDF"
                variant="secondary"
                accessibilityLabel="Download Report PDF"
                onPress={() => {
                  void handleDownload('PDF');
                }}
                disabled={!canDownload}
                loading={downloads.isDownloadInFlight(
                  EMPLOYEE_OVERALL_WEEKLY_REPORT_CODE,
                  'PDF',
                )}
                style={styles.downloadButton}
              />
            </View>
          ) : null}
          {activeProgress ? (
            <ReportDownloadProgressView
              progress={activeProgress}
              canCancel
              onCancel={() => downloads.cancelDownload(activeProgress.downloadId)}
            />
          ) : null}
          {downloadError ? (
            <Text style={[styles.message, { color: palette.error }]}>{downloadError}</Text>
          ) : null}
          {downloadNotice ? (
            <Text style={[styles.message, { color: palette.success }]}>
              {downloadNotice}
            </Text>
          ) : null}

          {loading && items.length === 0 ? (
            <AppLoader message="Loading employee overall weekly report" />
          ) : error && items.length === 0 ? (
            <ErrorState message={error} onRetry={() => fetchReport(page, false)} />
          ) : items.length === 0 ? (
            <EmptyState
              title="No data available"
              message="No employees matched this week and search."
            />
          ) : (
            <ScrollView
              style={styles.tableScroll}
              refreshControl={
                <RefreshControl
                  refreshing={refreshing}
                  onRefresh={() => {
                    setRefreshing(true);
                    void fetchReport(page, true);
                  }}
                  tintColor={palette.primary}
                />
              }
            >
              <ScrollView
                horizontal
                accessibilityLabel="Employee overall weekly attendance table"
              >
                <View
                  style={[
                    styles.table,
                    { backgroundColor: palette.surface, borderColor: palette.border },
                  ]}
                >
                  <View
                    style={[
                      styles.headerRow,
                      { borderBottomColor: palette.border, backgroundColor: palette.background },
                    ]}
                  >
                    <Text
                      accessibilityRole="header"
                      style={[styles.headerCell, styles.employeeCell, { width: columns.employee, color: palette.textMuted }]}
                    >
                      Employee Name
                    </Text>
                    {dayColumns.map(column => (
                      <View
                        key={column.date}
                        style={[styles.dayHeader, { width: columns.day }]}
                      >
                        <Text
                          accessibilityRole="header"
                          accessibilityLabel={`${column.weekday} ${column.dayLabel}`}
                          style={[styles.headerCell, { color: palette.textMuted }]}
                        >
                          {column.weekday}
                        </Text>
                        <Text style={[styles.dateCell, { color: palette.textSecondary }]}>
                          {column.dayLabel}
                        </Text>
                      </View>
                    ))}
                    <Text
                      accessibilityRole="header"
                      style={[styles.headerCell, { width: columns.daysWorked, color: palette.textMuted }]}
                    >
                      Total Working Days
                    </Text>
                    <Text
                      accessibilityRole="header"
                      style={[styles.headerCell, { width: columns.leave, color: palette.textMuted }]}
                    >
                      Total Leave Days
                    </Text>
                    <Text
                      accessibilityRole="header"
                      style={[styles.headerCell, { width: columns.lop, color: palette.textMuted }]}
                    >
                      Total LOP Days
                    </Text>
                    <Text
                      accessibilityRole="header"
                      style={[styles.headerCell, { width: columns.total, color: palette.textMuted }]}
                    >
                      Total Weekly Work Hours
                    </Text>
                  </View>
                  {items.map((item, index) => {
                    const hours = overallWeeklyWorkHours(item);
                    const daysWorked = overallWeeklyDaysWorked(item);
                    const leaveDays = overallWeeklyLeaveDays(item);
                    const lopDays = overallWeeklyLopDays(item);
                    return (
                      <View
                        key={String(item.employeeId)}
                        style={[
                          styles.dataRow,
                          {
                            borderBottomColor: palette.border,
                            borderBottomWidth:
                              index === items.length - 1 ? 0 : StyleSheet.hairlineWidth,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.employeeName,
                            { width: columns.employee, color: palette.textPrimary },
                          ]}
                        >
                          {item.employeeName}
                        </Text>
                        {dayColumns.map(column => {
                          const status = overallWeeklyDayStatus(item, column.date);
                          return (
                            <Text
                              key={column.date}
                              accessibilityLabel={`${item.employeeName}, ${column.weekday} ${column.dayLabel}, ${status}`}
                              style={[
                                styles.statusCell,
                                {
                                  width: columns.day,
                                  color: statusColor(status, palette),
                                },
                              ]}
                            >
                              {status}
                            </Text>
                          );
                        })}
                        <Text
                          accessibilityLabel={`${item.employeeName} total working days ${daysWorked}`}
                          style={[
                            styles.hoursCell,
                            { width: columns.daysWorked, color: palette.textPrimary },
                          ]}
                        >
                          {daysWorked}
                        </Text>
                        <Text
                          accessibilityLabel={`${item.employeeName} total leave days ${leaveDays}`}
                          style={[
                            styles.hoursCell,
                            { width: columns.leave, color: palette.textPrimary },
                          ]}
                        >
                          {leaveDays}
                        </Text>
                        <Text
                          accessibilityLabel={`${item.employeeName} total LOP days ${lopDays}`}
                          style={[
                            styles.hoursCell,
                            { width: columns.lop, color: palette.textPrimary },
                          ]}
                        >
                          {lopDays}
                        </Text>
                        <Text
                          accessibilityLabel={`${item.employeeName} total weekly work hours ${hours}`}
                          style={[
                            styles.hoursCell,
                            { width: columns.total, color: palette.textPrimary },
                          ]}
                        >
                          {hours}
                        </Text>
                      </View>
                    );
                  })}
                </View>
              </ScrollView>
              <View style={styles.pager}>
                <Text style={[styles.pagerLabel, { color: palette.textSecondary }]}>
                  {`Showing ${rangeStart}–${rangeEnd} of ${totalRecords}`}
                </Text>
                <View style={styles.pagerActions}>
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="Previous page"
                    disabled={page <= 1 || loading}
                    onPress={() => void fetchReport(page - 1)}
                  >
                    <Text
                      style={[
                        styles.pagerButton,
                        { color: page <= 1 ? palette.textMuted : palette.primary },
                      ]}
                    >
                      Previous
                    </Text>
                  </Pressable>
                  <Text style={[styles.pagerLabel, { color: palette.textPrimary }]}>
                    {`Page ${page} of ${totalPages}`}
                  </Text>
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="Next page"
                    disabled={page >= totalPages || loading}
                    onPress={() => void fetchReport(page + 1)}
                  >
                    <Text
                      style={[
                        styles.pagerButton,
                        {
                          color:
                            page >= totalPages ? palette.textMuted : palette.primary,
                        },
                      ]}
                    >
                      Next
                    </Text>
                  </Pressable>
                </View>
              </View>
            </ScrollView>
          )}
        </View>
      </AppScreen>
  );
}

export function EmployeeOverallWeeklyReportScreen(props: Props) {
  return (
    <ReportAccessGuard reportCode={REPORT_ACCESS_CODES.EMPLOYEE_OVERALL_WEEKLY}>
      <EmployeeOverallWeeklyReportBody {...props} />
    </ReportAccessGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  body: { flex: 1, paddingBottom: spacing.lg },
  weekBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  weekLabel: { ...typography.body, fontWeight: '700', flex: 1, textAlign: 'center' },
  chevron: { fontSize: 28, fontWeight: '600', paddingHorizontal: spacing.sm },
  thisWeek: {
    ...typography.caption,
    fontWeight: '700',
    marginTop: spacing.sm,
    marginBottom: spacing.md,
  },
  search: {
    ...typography.body,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    marginBottom: spacing.md,
  },
  downloadRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  downloadButton: { flex: 1 },
  message: {
    ...typography.caption,
    fontWeight: '600',
    marginBottom: spacing.sm,
  },
  tableScroll: { flex: 1 },
  table: {
    borderWidth: 1,
    borderRadius: 12,
    overflow: 'hidden',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    borderBottomWidth: StyleSheet.hairlineWidth,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    gap: spacing.xs,
  },
  dataRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    gap: spacing.xs,
  },
  headerCell: {
    ...typography.caption,
    fontWeight: '700',
  },
  dateCell: {
    ...typography.caption,
    marginTop: 2,
  },
  dayHeader: { gap: 0 },
  employeeCell: { paddingRight: spacing.xs },
  employeeName: {
    ...typography.body,
    fontWeight: '700',
    fontSize: 14,
  },
  statusCell: {
    ...typography.caption,
    fontWeight: '700',
  },
  hoursCell: {
    ...typography.body,
    fontWeight: '700',
    fontSize: 14,
  },
  pager: {
    marginTop: spacing.md,
    gap: spacing.sm,
  },
  pagerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  pagerLabel: { ...typography.caption, fontWeight: '600' },
  pagerButton: { ...typography.body, fontWeight: '700' },
});
